import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, Checkbox, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";
import NumberInput from "components/cck/NumberInput";

import { Language } from "imports/Utils";

import { paymentTermSchema, PaymentTermSchemaInput, PaymentTermSchemaOutput } from "./utils/paymentTerm.schema";

import { InsertPaymentTermDocument, UpdatePaymentTermDocument } from "__generated__/view/paymentterms/graphql/mutations.generated";

const PaymentTermCreateEdit = () => {

	const navigate = useNavigate();
	const location = useLocation();

	const { control, handleSubmit } = useForm<PaymentTermSchemaInput, unknown, PaymentTermSchemaOutput>({
		resolver: zodResolver(paymentTermSchema),
		defaultValues: {
			id: location.state?.paymentTerm?.id ?? null,
			days: location.state?.paymentTerm?.days ?? 1,
			externalReference: location.state?.paymentTerm?.externalReference ?? "",
			isDefault: location.state?.paymentTerm?.isDefault ?? false,
		},
	});

	const [ updatePaymentTerm, { loading: updateLoading } ] = useMutation(UpdatePaymentTermDocument);
	const [ insertPaymentTerm, { loading: insertLoading } ] = useMutation(InsertPaymentTermDocument);

	const save = (data: PaymentTermSchemaOutput) => {
		if (data.id) {
			updatePaymentTerm({
				variables: {
					input: data,
				},
				onCompleted: () => {
					navigate("/view/paymentterms");
				},
			});
		} else {
			insertPaymentTerm({
				variables: {
					input: data,
				},
				onCompleted: () => {
					navigate("/view/paymentterms");
				},
			});
		}
	};

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("PAYMENT_TERM")}
					<Helmet title={intl.formatMessage({ id: "PAYMENT_TERM" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/paymentterms")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={updateLoading || insertLoading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12 }}>
					<Card>
						<FormGroup
							label={Language("PAYMENT_TERM_DAYS")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="days"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("PAYMENT_TERM_EXTERNAL_REFERENCE")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="externalReference"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("PAYMENT_TERM_DEFAULT")}
							labelPlacement="left"
							overflow="hidden">
							<Controller
								name="isDefault"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default PaymentTermCreateEdit;
